import type { UserRole } from '../types/crm';
import { getActivePortalKind, setActivePortalKind, type DbPortalKind } from './supabase';

export type PortalKind =
  | 'crm'
  | 'it'
  | 'dem'
  | 'forms'
  | 'inventory'
  | 'accounts'
  | 'report'
  | 'purchasing'
  | 'design';

/** Single host with /crm and /it paths (portal.banteckhan.com.sg). */
export const IS_UNIFIED_PORTAL =
  (import.meta.env.VITE_UNIFIED_PORTAL ?? 'true').trim() !== 'false';

const ENV_PORTAL_KIND: PortalKind =
  (import.meta.env.VITE_PORTAL_KIND ?? 'crm').trim() === 'it' ? 'it' : 'crm';

const PORTAL_CHOICE_KEY = 'bth_portal_choice';

export function portalKindFromPath(pathname: string): PortalKind | null {
  if (!IS_UNIFIED_PORTAL) return ENV_PORTAL_KIND;
  if (pathname === '/dem' || pathname.startsWith('/dem/')) return 'dem';
  if (pathname === '/forms' || pathname.startsWith('/forms/')) return 'forms';
  if (pathname === '/inventory' || pathname.startsWith('/inventory/')) return 'report';
  if (pathname === '/accounts' || pathname.startsWith('/accounts/')) return 'accounts';
  if (pathname === '/report' || pathname.startsWith('/report/')) return 'report';
  if (pathname === '/purchasing' || pathname.startsWith('/purchasing/')) return 'purchasing';
  if (pathname === '/design' || pathname.startsWith('/design/')) return 'design';
  if (pathname === '/it' || pathname.startsWith('/it/')) return 'it';
  if (pathname === '/crm' || pathname.startsWith('/crm/')) return 'crm';
  return null;
}

export function resolveActivePortalKind(): PortalKind {
  if (!IS_UNIFIED_PORTAL) return ENV_PORTAL_KIND;
  return portalKindFromPath(window.location.pathname) ?? readPortalChoice() ?? 'crm';
}

export const PORTAL_KIND: PortalKind = IS_UNIFIED_PORTAL
  ? resolveActivePortalKind()
  : ENV_PORTAL_KIND;

export const IS_IT_PORTAL = resolveActivePortalKind() === 'it';

export function portalAppTitle(kind?: PortalKind | null): string {
  const fromEnv = (import.meta.env.VITE_APP_TITLE ?? '').trim();
  if (fromEnv && !IS_UNIFIED_PORTAL) return fromEnv;
  if (kind === 'dem') return 'BTH Data Entry Management';
  if (kind === 'forms') return 'BTH Application Form';
  if (kind === 'inventory') return 'BTH Inventory';
  if (kind === 'accounts') return 'BTH Accounts';
  if (kind === 'report') return 'BTH Report';
  if (kind === 'purchasing') return 'BTH Purchasing';
  if (kind === 'design') return 'BTH Design';
  if (kind === 'it') return 'BTH IT Management';
  if (kind === 'crm') return 'BTH CRM';
  if (IS_UNIFIED_PORTAL) return 'BTH Portal';
  return ENV_PORTAL_KIND === 'it' ? 'BTH IT Management' : 'BTH CRM';
}

export function portalAppTitleForPath(pathname: string): string {
  if (pathname === '/calendar' || pathname.startsWith('/calendar/')) {
    return 'BTH Calendar';
  }
  return portalAppTitle(portalKindFromPath(pathname));
}

/** @deprecated use portalAppTitleForPath */
export const APP_TITLE = portalAppTitleForPath(
  typeof window !== 'undefined' ? window.location.pathname : '/login',
);

export function portalBasePath(kind: PortalKind): string {
  return IS_UNIFIED_PORTAL ? `/${kind}` : '';
}

export function portalHomePath(kind: PortalKind): string {
  if (!IS_UNIFIED_PORTAL) return '/';
  // DEM index only redirects to analytics — land there directly to skip a hop.
  if (kind === 'dem') return '/dem/analytics';
  // No trailing slash — matches Route path="/crm" exactly and avoids a / → RootRedirect hop.
  return `/${kind}`;
}

export function portalPath(kind: PortalKind, subpath = ''): string {
  const clean = subpath.replace(/^\//, '');
  const base = portalBasePath(kind);
  if (!clean) return base || '/';
  return base ? `${base}/${clean}` : `/${clean}`;
}

/** Path under the active /crm or /it workspace (set by PortalProvider). */
export function activePortalPath(subpath = ''): string {
  return portalPath(getActivePortalKind(), subpath);
}

export function accountDetailPath(code: string): string {
  return activePortalPath(`accounts/${encodeURIComponent(code)}`);
}

export function canChoosePortal(role: UserRole): boolean {
  return isCrossPortalRole(role);
}

export function isCrossPortalRole(role: UserRole): boolean {
  return role === 'it' || role === 'management';
}

export function readPortalChoice(): PortalKind | null {
  try {
    const value = sessionStorage.getItem(PORTAL_CHOICE_KEY);
    if (value === 'inventory') return 'report';
    return value === 'it' ||
      value === 'crm' ||
      value === 'dem' ||
      value === 'forms' ||
      value === 'accounts' ||
      value === 'report' ||
      value === 'purchasing' ||
      value === 'design'
      ? value
      : null;
  } catch {
    return null;
  }
}

export function writePortalChoice(kind: PortalKind): void {
  try {
    sessionStorage.setItem(PORTAL_CHOICE_KEY, kind);
  } catch {
    /* ignore */
  }
}

/** In-memory soft-entry marker — survives the choose→portal paint even if sessionStorage is flaky. */
let softPortalEntryKind: PortalKind | null = null;
/** Grace window after choose→portal SPA navigation (independent of clearPortalChoice). */
let softPortalSwitchUntil = 0;
/**
 * Keep Choose Portal's Verifying overlay up until navigate unmounts it — even after
 * accessStatus flips to allowed — so the destination never paints a second Verifying.
 */
let chooserVerifyingHold = false;

export function setChooserVerifyingHold(held: boolean): void {
  chooserVerifyingHold = held;
}

export function isChooserVerifyingHold(): boolean {
  return chooserVerifyingHold;
}

export function markSoftPortalEntry(kind: PortalKind): void {
  softPortalEntryKind = kind;
  writePortalChoice(kind);
}

/** Mark an in-flight soft portal switch so auth verify must not flash Verifying or overwrite profile. */
export function markSoftPortalSwitch(kind: PortalKind): void {
  markSoftPortalEntry(kind);
  // Longer on mobile — Safari auth / route settle is slower than desktop.
  const mobile =
    typeof window !== 'undefined' &&
    (window.matchMedia('(max-width: 900px)').matches || window.matchMedia('(pointer: coarse)').matches);
  softPortalSwitchUntil = Date.now() + (mobile ? 12_000 : 5_000);
}

export function clearSoftPortalEntry(): void {
  softPortalEntryKind = null;
  softPortalSwitchUntil = 0;
}

/** True when this path is the portal the user just picked (or a matching stored choice). */
export function isSoftPortalEntryPath(pathname: string): boolean {
  const pathKind = portalKindFromPath(pathname);
  if (!pathKind) return false;
  if (softPortalEntryKind === pathKind) return true;
  return readPortalChoice() === pathKind;
}

/** True during the grace window after useGoToPortal / enterPortal. */
export function isSoftPortalSwitchActive(pathname: string): boolean {
  if (Date.now() > softPortalSwitchUntil) return false;
  // Kind is set even before navigate() commits; path may still be /choose-portal.
  if (softPortalEntryKind != null) return true;
  return isSoftPortalEntryPath(pathname);
}

/** Soft choose→portal in flight (kind marked, regardless of current URL). */
export function isSoftPortalEntryPending(): boolean {
  if (softPortalEntryKind != null) return true;
  return Date.now() <= softPortalSwitchUntil;
}

/**
 * Short window after markSoftPortalSwitch / chooser hold — use for deferring SW
 * update/reload so choose→CRM does not double-flash Verifying when notifications are on.
 * Unlike isSoftPortalEntryPending, this clears when the grace timer ends.
 */
export function isSoftPortalSwitchInGrace(): boolean {
  return isChooserVerifyingHold() || Date.now() <= softPortalSwitchUntil;
}

export function softPortalEntryTarget(): PortalKind | null {
  return softPortalEntryKind;
}

export function clearPortalChoice(): void {
  softPortalEntryKind = null;
  softPortalSwitchUntil = 0;
  try {
    sessionStorage.removeItem(PORTAL_CHOICE_KEY);
  } catch {
    /* ignore */
  }
}

function portalOrigin(): string {
  const fromEnv = (import.meta.env.VITE_PORTAL_BASE_URL ?? '').trim();
  if (fromEnv) return fromEnv.replace(/\/$/, '');
  return window.location.origin;
}

export function crmPortalUrl(): string {
  if (IS_UNIFIED_PORTAL) return `${portalOrigin()}/crm`;
  const fromEnv = (import.meta.env.VITE_CRM_PORTAL_URL ?? '').trim();
  if (fromEnv) return fromEnv.replace(/\/$/, '');
  if (ENV_PORTAL_KIND === 'crm') return window.location.origin;
  return 'https://crm.banteckhan.com.sg';
}

export function itPortalUrl(): string {
  if (IS_UNIFIED_PORTAL) return `${portalOrigin()}/it`;
  const fromEnv = (import.meta.env.VITE_IT_PORTAL_URL ?? '').trim();
  if (fromEnv) return fromEnv.replace(/\/$/, '');
  if (ENV_PORTAL_KIND === 'it') return window.location.origin;
  return 'https://it.banteckhan.com.sg';
}

export type GoToPortalOptions = {
  /** Soft SPA navigation on the unified host (avoids full page reload). */
  navigate?: (to: string) => void;
  /**
   * Re-verify allowlist/profile for the new portal. Call this so access flips to
   * `checking` before the route changes — otherwise RequireAuth can bounce back
   * to /choose-portal while status is still `choose_portal`.
   */
  refreshAccess?: () => void;
};

/** Navigate to the chosen portal (path-based or legacy subdomain). */
export function goToPortal(kind: PortalKind, options?: GoToPortalOptions): void {
  writePortalChoice(kind);
  // Soft switches must flip the DB client before child pages fetch.
  setActivePortalKind(dbKindForPortalChoice(kind));
  const home = portalHomePath(kind);
  if (IS_UNIFIED_PORTAL) {
    if (options?.navigate) {
      // Navigate first so access re-verify reads the new path after awaits.
      options.navigate(home);
      options.refreshAccess?.();
      return;
    }
    window.location.assign(home);
    return;
  }
  const target = kind === 'it' ? itPortalUrl() : crmPortalUrl();
  const targetOrigin = new URL(target, window.location.href).origin;
  const path = targetOrigin === window.location.origin ? '/' : `${target.replace(/\/$/, '')}/`;
  window.location.assign(path);
}

function dbKindForPortalChoice(kind: PortalKind): DbPortalKind {
  if (kind === 'it') return 'it';
  if (kind === 'dem') return 'dem';
  if (kind === 'forms') return 'forms';
  return 'crm';
}

export function portalAllowsRole(_role: UserRole, _kind: PortalKind = getActivePortalKind()): boolean {
  return true;
}

export function portalAccessDeniedMessage(kind: PortalKind = getActivePortalKind()): string {
  if (kind === 'dem') {
    return 'You are not on the Data Entry Management allowlist. Ask an admin to add your email to the DEM Web allowlist.';
  }
  if (kind === 'forms') {
    return 'You are not on the Application Form allowlist. Ask an admin to grant Forms access in User management.';
  }
  if (kind === 'inventory') {
    return 'You are not on the Report allowlist. Ask an admin to grant Report access in Admin Hub → User management.';
  }
  if (kind === 'accounts') {
    return 'Accounts is limited to IT and management. Ask an admin if you need access.';
  }
  if (kind === 'report') {
    return 'You are not on the Report allowlist. Ask an admin to grant Report access in Admin Hub → User management.';
  }
  if (kind === 'purchasing') {
    return 'You are not on the Purchasing allowlist. Ask an admin to grant Purchasing access in Admin Hub → User management.';
  }
  if (kind === 'design') {
    return 'You are not on the Design allowlist. Ask an admin to grant Design access in Admin Hub → User management.';
  }
  if (kind === 'it') {
    return 'You are not on the IT Management allowlist. Ask an admin to add your email under Settings in the IT workspace.';
  }
  return 'You are not on the Sales CRM allowlist. Ask an admin to add your email under Settings in the CRM workspace.';
}

export function portalStorageRoot(kind: PortalKind = getActivePortalKind()): string {
  const schema = kind === 'it' ? 'it' : 'crm';
  return schema === 'crm' ? '' : schema;
}

export function scopedStoragePath(relativePath: string, kind: PortalKind = getActivePortalKind()): string {
  const root = portalStorageRoot(kind);
  return root ? `${root}/${relativePath}` : relativePath;
}

export function isNeutralAuthPath(pathname: string): boolean {
  return (
    pathname === '/' ||
    pathname === '/login' ||
    pathname === '/choose-portal' ||
    pathname === '/not-authorized' ||
    pathname.startsWith('/auth/')
  );
}

/** Hidden calendar workspace (not a portal kind; deep-link only). */
export function isCalendarPath(pathname: string): boolean {
  return pathname === '/calendar' || pathname.startsWith('/calendar/');
}

const POST_LOGIN_RETURN_KEY = 'bth_post_login_return';

export function rememberPostLoginReturn(pathname: string, search = '', hash = ''): void {
  if (!pathname || isNeutralAuthPath(pathname)) return;
  try {
    sessionStorage.setItem(POST_LOGIN_RETURN_KEY, `${pathname}${search}${hash}`);
  } catch {
    /* ignore */
  }
}

export function consumePostLoginReturn(): string | null {
  try {
    const value = sessionStorage.getItem(POST_LOGIN_RETURN_KEY);
    sessionStorage.removeItem(POST_LOGIN_RETURN_KEY);
    if (!value || isNeutralAuthPath(value.split('?')[0]?.split('#')[0] ?? '')) return null;
    return value;
  } catch {
    return null;
  }
}
